import React from "react";
import {
  act,
  fireEvent,
  render,
  screen,
  waitFor,
} from "@testing-library/react";
import "@testing-library/jest-dom";
import FileUploader from "./FileUploader";

/**
 * FE #1461: Meridian's "Generate Document" button is gated on
 * ConfigPanel.tsx's `hasActiveSourceUploads`, which is fed entirely by
 * FileUploader's `onUploadActivityChange(activeUploadCount > 0)` callback.
 * The moment an upload transitions to `uploadStatus: "interrupted"` (a
 * network drop, or a job restored on mount in a non-terminal state),
 * `setUploadOperationActive(id, false)` fires in the same code path --
 * so the source stops counting as "active" even though it was never
 * indexed and sits behind a "Retry upload" control. ConfigPanel's
 * `hasIndexingIssue` cannot see this either: "interrupted" is a
 * FileUploader-local `uploadStatus`, never a legal `indexStatus` value.
 * Net effect: Generate re-enables while a source upload is interrupted.
 *
 * These tests exercise `onUploadActivityChange` directly against the real
 * interruption path (the same network-drop shape as
 * FileUploader.retry.test.tsx's "offers one-click retry" case), which is
 * the actual seam the fix lives at -- ConfigPanel.tsx cannot be rendered in
 * a unit test (it has no existing test file), so this is the level closest
 * to the reported bug that is feasible to prove directly.
 */

const mockAuthFetch = jest.fn();
let mockDropFiles: (files: File[]) => void;

jest.mock("../../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => mockAuthFetch,
}));
jest.mock("../../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "workspace-1" } }),
}));
jest.mock("react-dropzone", () => ({
  useDropzone: ({ onDrop }: { onDrop: (files: File[]) => void }) => {
    mockDropFiles = onDrop;
    return { getRootProps: () => ({}), getInputProps: () => ({}) };
  },
}));

const endpoint = "/api/workspace/meridian/documents/upload/source-document";

function renderUploader(onUploadActivityChange: (active: boolean) => void) {
  return render(
    <FileUploader
      documentType="Source"
      uploadEndpoint={endpoint}
      metadata={{ documentId: 9, sourceFieldId: 4 }}
      onUploadActivityChange={onUploadActivityChange}
    />,
  );
}

function registerId(options: RequestInit | undefined): string {
  return JSON.parse(String(options?.body)).client_upload_id;
}

beforeEach(() => {
  localStorage.clear();
  mockAuthFetch.mockReset();
  jest.spyOn(console, "error").mockImplementation(() => undefined);
  Object.defineProperty(URL, "createObjectURL", {
    configurable: true,
    value: jest.fn(() => "blob:source-test"),
  });
});

afterEach(() => {
  jest.restoreAllMocks();
});

it("keeps reporting upload activity while a transfer is interrupted, not just while it is in flight", async () => {
  const registeredIds: string[] = [];
  const uploadedIds: string[] = [];
  const onUploadActivityChange = jest.fn();
  mockAuthFetch.mockImplementation(
    async (_url: string, tag: string, options?: RequestInit) => {
      if (tag === "register source upload") {
        const id = registerId(options);
        registeredIds.push(id);
        return { job_id: id };
      }
      if (tag === "upload file") {
        const id = (options?.body as FormData).get("upload_job_id") as string;
        uploadedIds.push(id);
        if (uploadedIds.length === 1) throw new Error("Network interrupted");
        return { job_id: id };
      }
      if (tag === "fetch job status") {
        return {
          status: "completed",
          fileReference: { id: 43, originalName: "source.pdf" },
        };
      }
      throw new Error(`Unexpected request: ${tag}`);
    },
  );

  renderUploader(onUploadActivityChange);
  await act(async () => {
    mockDropFiles([
      new File(["source"], "source.pdf", { type: "application/pdf" }),
    ]);
  });

  // The card is showing the interrupted state, not a resolved one.
  expect(
    await screen.findByText("Upload interrupted. Retry with this file."),
  ).toBeInTheDocument();

  // The bug this pins: the moment the transfer is interrupted, the async
  // operation itself finishes (setUploadOperationActive(..., false) fires
  // in the same code path), so a naive activeUploadCount > 0 signal drops
  // to false right here -- even though this source was never indexed and
  // Generate should still be blocked on it.
  expect(onUploadActivityChange).toHaveBeenLastCalledWith(true);

  // Retry, and once the upload completes for real, activity clears --
  // proving the fix does not get stuck reporting "active" forever.
  const retry = await screen.findByRole("button", { name: "Retry upload" });
  fireEvent.click(retry);
  await waitFor(() => expect(uploadedIds).toHaveLength(2));
  await waitFor(() =>
    expect(onUploadActivityChange).toHaveBeenLastCalledWith(false),
  );
});

it("keeps reporting upload activity for a job restored on mount in the interrupted state", async () => {
  const workspaceId = "workspace-1";
  const jobId = "restored-job-1";
  localStorage.setItem(
    `mm_upload_jobs_${workspaceId}`,
    JSON.stringify([
      {
        jobId,
        fileName: "restored.pdf",
        fileSize: 1234,
        clientUploadAttemptId: "attempt-1",
        registrationConfirmed: true,
        uploadEndpoint: endpoint,
        documentId: 9,
        sourceFieldId: 4,
        startedAt: Date.now(),
      },
    ]),
  );

  const onUploadActivityChange = jest.fn();
  mockAuthFetch.mockImplementation(async (_url: string, tag: string) => {
    if (tag === "fetch restored job status") {
      // The job was registered but the transfer itself never completed
      // (browser closed/offline mid-upload) -- restored as interrupted,
      // not silently dropped.
      return { status: "awaiting_upload" };
    }
    throw new Error(`Unexpected request: ${tag}`);
  });

  renderUploader(onUploadActivityChange);

  expect(
    await screen.findByText(
      "Upload interrupted. Select the same file again to retry.",
    ),
  ).toBeInTheDocument();

  expect(onUploadActivityChange).toHaveBeenLastCalledWith(true);
});
